/**
 * ENTITIES section: the type dispatcher every entity passes through
 * (§3.1). Each known drafting type routes to its native serializer; the
 * fallback keeps unknown payloads lossless by writing their tessellated
 * outline (the only safe representation for BIM-side citizens).
 */

import type { Entity } from '../../../../state/store';
import { entityToPolyline } from '../../../../core/commands/runners/lib';
import type { DxfWriteContext } from '../types';
import type { DxfStreamWriter } from '../DxfStreamWriter';
import { entityPrologue } from '../serializers/common';
import { writeLine } from '../serializers/line';
import { writeCircle, writeArc, writeEllipse } from '../serializers/circleArcEllipse';
import { writeLwpolyline, polylineVertices } from '../serializers/polyline';
import { writePoint } from '../serializers/point';
import { writeText, writeMText } from '../serializers/textMtext';
import { writeDimension } from '../serializers/dimension';
import { writeHatch } from '../serializers/hatch';
import { writeInsert, writeAttdef } from '../serializers/insertAttrib';
import { writeTable } from '../serializers/table';
import { writeLeader } from '../serializers/leaderMleader';
import { writeSpline } from '../serializers/splineCurve';
import { writeSolid } from '../serializers/wipeoutGradient';
import { writeToleranceEntity } from '../serializers/tolerance';

/** Serialize one entity into the current stream. */
export function writeEntity(w: DxfStreamWriter, ctx: DxfWriteContext, e: Entity, owner: string): void {
    switch (e.type) {
        case 'line': writeLine(w, ctx, e, owner); return;
        case 'circle': writeCircle(w, ctx, e, owner); return;
        case 'arc': writeArc(w, ctx, e, owner); return;
        case 'ellipse': writeEllipse(w, ctx, e, owner); return;
        case 'point': writePoint(w, ctx, e, owner); return;
        case 'text': writeText(w, ctx, e, owner); return;
        case 'mtext': writeMText(w, ctx, e, owner); return;
        case 'polyline':
        case 'polygon':
        case 'rectangle': writeLwpolyline(w, ctx, e, owner); return;
        case 'dimension': writeDimension(w, ctx, e, owner); return;
        case 'tolerance': writeToleranceEntity(w, ctx, e, owner); return;
        case 'hatch': writeHatch(w, ctx, e, owner); return;
        case 'insert':
        case 'block': writeInsert(w, ctx, e, owner); return;
        case 'attdef': writeAttdef(w, ctx, e, owner); return;
        case 'table': writeTable(w, ctx, e, owner); return;
        case 'leader': writeLeader(w, ctx, e, owner); return;
        case 'curve':
        case 'nurbs-curve':
        case 'spline': writeSpline(w, ctx, e, owner); return;
        case 'solid': writeSolid(w, ctx, e, owner); return;
        default: writeFallbackPolyline(w, ctx, e, owner); return;
    }
}

/** Lossless fallback: tessellate the geometry into an outline polyline. */
function writeFallbackPolyline(w: DxfStreamWriter, ctx: DxfWriteContext, e: Entity, owner: string): void {
    const poly = entityToPolyline(e);
    if (!poly || poly.length < 2) return;
    const shell: Entity = { ...e, id: `${e.id}#fb`, type: 'polyline', data: { vertices: poly, closed: false } };
    const { verts } = polylineVertices(shell);
    if (verts.length < 2) return;
    entityPrologue(w, ctx, shell, 'LWPOLYLINE', owner);
    w.pair(100, 'AcDbPolyline');
    w.pair(90, verts.length);
    w.pair(70, 0);
    verts.forEach((v) => w.pair(10, v[0] ?? 0).pair(20, v[1] ?? 0));
}

export function writeEntitiesSection(w: DxfStreamWriter, ctx: DxfWriteContext, entities: Entity[]): void {
    w.pair(0, 'SECTION').pair(2, 'ENTITIES');
    entities.forEach((e) => writeEntity(w, ctx, e, ctx.modelSpaceRecord));
    w.pair(0, 'ENDSEC');
}
